import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../../../firebase/config';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';

import { getEstadoRenovacion, getPersonaChipClass } from '../../../../utils/styles.js';
import { useAuth } from '../../../hooks/useAuth'; 

// NUESTROS COMPONENTES DE ARQUITECTURA MODULAR COMUNES
import SortableHeader from '../common/SortableHeader';
import PaginatorFooter from '../common/PaginatorFooter';
import ColumnSelector from '../common/ColumnSelector';
import TableContainer from '../common/TableContainer';
import Icons from '../common/Icons';

// MODALES EXISTENTES EN EL PROYECTO
import ModalDetallePoliza from '../../../ui/ModalDetallePoliza.jsx';
import EditarRenovacion from '../../../ui/EditarRenovacion.jsx';
import RenovarPoliza from '../../../ui/RenovarPoliza.jsx';
import ModalDetalleDeuda from '../../../ui/ModalDetalleDeuda.jsx';
import ModalFichaCliente from '../../../ui/ModalFichaCliente.jsx';
// 🟢 IMPORTAMOS EL MODAL DE EXPEDIENTE UNIFICADO DE SINIESTROS
import ModalDetalleSiniestros from '../../../ui/ModalDetalleSiniestros.jsx';

// FILTROS Y CONTROLADOR
import FiltrosDinamicos from '../../../ui/FiltrosDinamicos.jsx';
import { useRenovacionesTable } from './useRenovacionesTable';

// --- FILA MEMOIZADA ---
const TableRow = memo(({ 
    p, 
    isSelected, 
    isLocked, 
    isLockedByMe, 
    columnVisibility, 
    onSelect, 
    onAction, 
    onUnlock, 
    onShowDebt,
    onShowSiniestro // 🟢 RECIBIMOS LA PROP DE SINIESTRO
}) => {
    const today = useMemo(() => {
        const d = new Date();
        d.setUTCHours(0, 0, 0, 0);
        return d;
    }, []);

    const { dateClass, formattedDate } = useMemo(() => {
        if (!p.vencimientoDate) return { dateClass: '', formattedDate: 'N/A' };
        const vDate = new Date(p.vencimientoDate + "T00:00:00Z");
        const isVencida = vDate < today;
        return {
            dateClass: isVencida ? 'text-red-600 font-bold' : 'text-orange-700 font-semibold',
            formattedDate: p.vencimientoDate.split('-').reverse().join('/')
        };
    }, [p.vencimientoDate, today]);

    return (
        <tr 
            onClick={() => !isLocked && onSelect(p.id)}
            className={`group cursor-pointer transition-all duration-75 border-b border-slate-100
                ${isLocked ? 'opacity-40 select-none bg-slate-50' : ''} 
                ${isSelected ? 'bg-blue-100/60' : 'hover:bg-slate-50'}`}
        >
            {columnVisibility.vencimientoDate && (
                <td className={`p-2.5 px-3 text-sm whitespace-nowrap border-b border-r border-slate-100 text-center ${dateClass}`}>
                    {formattedDate}
                </td>
            )}

            {columnVisibility.nPoliza && (
                <td className="p-2.5 px-3 text-sm text-slate-900 font-bold text-center border-b border-r border-slate-100 tabular-nums">
                    {p.nPoliza}
                </td>
            )}

            {columnVisibility.compania && (
                <td className="p-2.5 px-3 border-b border-r border-slate-100 text-center">
                    <div className="flex items-center justify-center">
                        <div className="flex items-center gap-2 px-3 py-1 bg-blue-50 border border-blue-100 rounded-lg w-fit group-hover:bg-white transition-colors shadow-sm">
                            <div className="text-blue-400"><Icons.Company /></div>
                            <span className="text-[11px] text-blue-800 font-bold uppercase truncate max-w-[160px]">{p.compania}</span>
                        </div>
                    </div>
                </td>
            )}

            {columnVisibility.displayNombre && (
                <td className="p-2.5 px-4 text-sm text-slate-900 font-bold whitespace-nowrap truncate border-b border-r border-slate-100 text-left" title={p.displayNombre}>
                    <div className="flex items-center justify-between gap-4">
                        <span className="truncate">{p.displayNombre}</span>
                        
                        <div className="flex items-center gap-1.5 shrink-0">
                            {/* PÓLIZA PACK */}
                            {p.isPolizaPack && (
                                <div className="flex items-center text-amber-500 bg-amber-50 p-1 rounded-md border border-amber-200/80" title={`Póliza Pack: ${p.referenciasPolizaPack || 'S/D'}`}>
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.828a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                    </svg>
                                </div>
                            )}

                            {/* 🔴 BADGE DEUDA */}
                            {p.tieneDeuda && (
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); onShowDebt(p); }}
                                    className="group/deuda flex items-center gap-1 px-2.5 py-0.5 bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200/90 rounded-full text-[10px] font-black uppercase transition-all shadow-2xs hover:scale-105 active:scale-95 cursor-pointer animate-pulse"
                                    title="Ver detalle de Deuda"
                                >
                                    <svg className="w-3.5 h-3.5 text-rose-600 transition-transform group-hover/deuda:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 8v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <span className="tracking-tight">Deuda</span>
                                </button>
                            )}

                            {/* 🟠 BADGE SINIESTRO */}
                            {p.tieneSiniestro && (
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); onShowSiniestro(p); }}
                                    className="group/siniestro flex items-center gap-1 px-2.5 py-0.5 bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200/90 rounded-full text-[10px] font-black uppercase transition-all shadow-2xs hover:scale-105 active:scale-95 cursor-pointer"
                                    title="Ver expediente de Siniestro"
                                >
                                    <svg className="w-3.5 h-3.5 text-amber-600 transition-transform group-hover/siniestro:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                    </svg>
                                    <span className="tracking-tight">Siniestro</span>
                                </button>
                            )}
                        </div>
                    </div>
                </td>
            )}

            {columnVisibility.displayDoc && (
                <td className="p-2.5 px-3 text-sm text-slate-600 font-mono text-center border-b border-r border-slate-100">{p.displayDoc}</td>
            )}

            {columnVisibility.riesgo && (
                <td className="p-2.5 px-4 text-sm text-slate-700 whitespace-nowrap border-b border-r border-slate-100 text-left">
                    <div className="flex items-center justify-between gap-4">
                        <div className="flex flex-col truncate text-left">
                            <span className="font-bold text-slate-800 tracking-tight">{p.riesgo}</span>
                            <span className="text-[11px] text-slate-500 font-medium uppercase truncate mt-0.5">{p.subriesgo || 'General'}</span>
                        </div>
                        <div className="shrink-0">
                            {p.formaRenovacion === 'AUTOMÁTICA' ? (
                                <div className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center shadow-sm border border-emerald-200" title="Renovación Automática"><svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" /></svg></div>
                            ) : (
                                <div className="w-6 h-6 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center shadow-sm border border-slate-200" title="Renovación Manual"><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3"><path strokeLinecap="round" strokeLinejoin="round" d="M7 11.5V14m0-2.5v-6a1.5 1.5 0 113 0m-3 6a1.5 1.5 0 00-3 0v2a7.5 7.5 0 0015 0v-5a1.5 1.5 0 00-3 0m-6-3V11m0-5.5v-1a1.5 1.5 0 013 0v1m0 0V11" /></svg></div>
                            )}
                        </div>
                    </div>
                </td>
            )}

            {columnVisibility.formaPago && (
                <td className="p-2.5 px-3 border-b border-r border-slate-100 text-center">
                    <div className="flex items-center justify-center px-3 h-[26px] bg-blue-50 border border-blue-100 rounded-lg w-fit mx-auto">
                        <span className="text-[11px] text-blue-800 font-bold uppercase">{p.formaPago || '-'}</span>
                    </div>
                </td>
            )}

            {columnVisibility.estadoGestion && (
                <td className="p-2.5 px-3 text-center border-b border-r border-slate-100">
                    <span className={`px-4 py-1.5 rounded-full text-[11px] font-bold uppercase w-fit ${getEstadoRenovacion(p.estadoGestionRenovacion || 'PENDIENTE')}`}>
                        {p.estadoGestionRenovacion || 'PENDIENTE'}
                    </span>
                </td>
            )}

            {columnVisibility.gestionadoPor && (
                <td className="p-2.5 px-3 text-center border-b border-r border-slate-100">
                    {p.gestionadoPor ? (
                        <div className={`flex items-center gap-2 px-2.5 py-1 rounded-full w-fit shadow-sm border border-black/5 mx-auto ${getPersonaChipClass(p.gestionadoPor)}`}>
                            <Icons.User />
                            <span className="text-[10px] font-bold uppercase">{p.gestionadoPor}</span>
                        </div>
                    ) : <span className="text-slate-200 text-sm font-bold">-</span>}
                </td>
            )}

            {columnVisibility.canal && (
                <td className="p-2.5 px-3 text-sm text-slate-700 whitespace-nowrap border-b border-r border-slate-100 text-left">
                    <div className="flex flex-col truncate text-left">
                        <span className="font-bold text-slate-800 tracking-tight">{p.canal}</span>
                        <span className="text-[11px] text-slate-500 font-medium uppercase truncate mt-0.5">{p.canalNombre || p.subcanal || '-'}</span>
                    </div>
                </td>
            )}

            {/* Acciones */}
            <td className="p-2.5 px-4 text-right sticky right-0 transition-colors z-20 border-b border-l border-slate-100 shadow-[-10px_0_15px_-5px_rgba(0,0,0,0.03)] bg-white group-hover:bg-slate-50">
                <div className="flex justify-end items-center gap-2">
                    <button type="button" onClick={(e) => { e.stopPropagation(); onAction(p, 'ficha'); }} className="p-1.5 text-slate-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-all active:scale-90" title="Ficha Técnica Cliente"><Icons.User /></button>
                    <button type="button" onClick={(e) => { e.stopPropagation(); onAction(p, 'view'); }} className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all active:scale-90" title="Ver Detalles"><Icons.Policy /></button>
                    {isLocked ? (
                        <div className="flex items-center gap-2 text-amber-600 bg-amber-50 px-3 py-1.5 rounded-lg border border-amber-100 animate-pulse shadow-sm">
                            <Icons.Lock /> <span className="text-[9px] font-black uppercase tracking-tighter">{p.bloqueo?.userName || 'Ocupado'}</span>
                        </div>
                    ) : (
                        <>
                            <button type="button" onClick={(e) => { e.stopPropagation(); onAction(p, 'edit'); }} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all active:scale-90" title="Gestionar Renovación"><Icons.Edit /></button>
                            <button type="button" onClick={(e) => { e.stopPropagation(); onAction(p, 'renew'); }} className="p-1.5 text-emerald-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-all active:scale-90" title="Renovar Póliza"><Icons.ChevronRight /></button>
                            {isLockedByMe && (
                                <button type="button" onClick={(e) => { e.stopPropagation(); onUnlock(p.path); }} className="p-1.5 text-red-300 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all" title="Liberar Caso">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path></svg>
                                </button>
                            )}
                        </>
                    )}
                </div>
            </td>
        </tr>
    );
});
TableRow.displayName = 'TableRow';

// --- ORQUESTADOR VISUAL MÁSTER ---
const RenovacionPolizas = ({ onRenovacionSuccess }) => {
    const navigate = useNavigate();
    const { user } = useAuth();

    // 🟢 ESTADOS PARA EL MODAL DE SINIESTROS
    const [showSiniestroModal, setShowSiniestroModal] = useState(false);
    const [siniestrosTarget, setSiniestrosTarget] = useState([]);
    const [siniestroPoliza, setSiniestroPoliza] = useState(null);
    const [siniestroClient, setSiniestroClient] = useState(null);

    // Consumimos nuestro cerebro estructurado de Renovaciones
    const {
        loading,
        needsHydration,
        clientsCache,
        activePoliza,
        showDetailModal,
        showEditModal,
        showRenewModal,
        showDebtModal,
        showFichaModal,
        tableState,
        itemsTable,
        activeItemPathRef,
        setShowDetailModal,
        setShowEditModal,
        setShowRenewModal,
        setShowDebtModal,
        setShowFichaModal,
        setActivePoliza,
        handleAction,
        handleShowDebt,
        unlockPoliza
    } = useRenovacionesTable();

    // Desestructuramos el estado de tabla genérico
    const {
        sortConfig,
        columnVisibility,
        currentPage,
        setCurrentPage,
        selectedRowId,
        setSelectedRowId,
        filterValues,
        filteredData,
        paginatedData,
        totalPages,
        handleSort,
        toggleColumn,
        handleFilterChange,
        checkIsLocked: checkIsItemLocked
    } = tableState;

    const columnLabels = {
        vencimientoDate: "Vencimiento", nPoliza: "N° Póliza", compania: "Compañía",
        displayNombre: "Cliente", displayDoc: "DNI/CUIT", riesgo: "Riesgo/Subr", formaPago: "Pago",
        estadoGestion: "Estado", gestionadoPor: "Resp.", canal: "Canal"
    };

    // 🟢 HANDLER PARA ABRIR MODAL DE SINIESTROS Y CARGAR SUS DATOS EN REAL-TIME
    const handleShowSiniestro = useCallback((polizaItem) => {
        const cId = polizaItem.clienteId;
        const pId = polizaItem.id;

        if (!cId || !pId) return;

        const clientData = { id: cId, name: polizaItem.displayNombre, ...(clientsCache[cId] || {}) };
        
        setSiniestroPoliza(polizaItem);
        setSiniestroClient(clientData);

        // Subscripción en vivo a la subcolección de siniestros de esa póliza
        const path = `public/data/clientes/${cId}/polizas/${pId}/siniestros`;
        const q = query(collection(db, path), orderBy('fechaSiniestro', 'desc'));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setSiniestrosTarget(data);
            setShowSiniestroModal(true);
        }, (error) => {
            console.error("Error al cargar los siniestros del caso:", error);
        });

        return () => unsubscribe();
    }, [clientsCache]);

    // Liberación automática por descarga involuntaria de pestaña
    useEffect(() => {
        const handleUnload = () => { if (activeItemPathRef.current) unlockPoliza(activeItemPathRef.current); };
        window.addEventListener('beforeunload', handleUnload);
        return () => window.removeEventListener('beforeunload', handleUnload);
    }, [unlockPoliza, activeItemPathRef]);

    // Configuración de Filtros Dinámicos
    const filterConfig = useMemo(() => {
        const getUnique = (list, key) => [...new Set(list.map(p => p[key]))].filter(Boolean).sort().map(v => ({label: v, value: v}));
        
        const selCanal = filterValues.canal || [];
        const dataForCanal = selCanal.length > 0 ? itemsTable.filter(d => selCanal.includes(d.canal)) : itemsTable;
        const canalNombreOptions = [...new Set(dataForCanal.map(d => d.canalNombre))].filter(Boolean).sort().map(v => ({label: v, value: v}));
        const selCanalNombre = filterValues.canalNombre || [];
        const dataForSubcanal = selCanalNombre.length > 0 ? dataForCanal.filter(d => selCanalNombre.includes(d.canalNombre)) : dataForCanal;
        const subcanalOptions = [...new Set(dataForSubcanal.map(d => d.subcanalNombre))].filter(Boolean).sort().map(v => ({label: v, value: v}));

        return [
            { name: 'search', label: 'Buscador', type: 'text', placeholder: 'Cliente o Póliza...', className: 'w-72' },
            { name: 'riesgo', label: 'Riesgo', type: 'select', isMulti: true, options: getUnique(itemsTable, 'riesgo') },
            { name: 'subriesgo', label: 'Subriesgo', type: 'select', isMulti: true, options: getUnique(itemsTable, 'subriesgo') },
            { name: 'compania', label: 'Compañía', type: 'select', isMulti: true, options: getUnique(itemsTable, 'compania') },
            { name: 'formaPago', label: 'Forma Pago', type: 'select', isMulti: true, options: getUnique(itemsTable, 'formaPago') },
            { name: 'estadoGestion', label: 'Estado', type: 'select', isMulti: true, options: getUnique(itemsTable, 'estadoGestion') }, 
            { name: 'gestionadoPor', label: 'Responsable', type: 'select', isMulti: true, options: getUnique(itemsTable, 'gestionadoPor') },
            { name: 'canal', label: 'Canal', type: 'select', isMulti: true, options: getUnique(itemsTable, 'canal') },
            { name: 'canalNombre', label: 'Nombre Canal', type: 'select', isMulti: true, options: canalNombreOptions },
            { name: 'subcanalNombre', label: 'Subcanal / Asociado', type: 'select', isMulti: true, options: subcanalOptions },
            { name: 'vencimientoDate', label: 'Vencimiento', type: 'date-range', className: 'w-64' },
        ];
    }, [itemsTable, filterValues]);

    if (loading || needsHydration) return (
        <div className="flex flex-col items-center justify-center h-screen bg-white space-y-6">
            <div className="w-16 h-16 border-4 border-blue-600/20 border-t-blue-600 rounded-full animate-spin"></div>
            <div className="text-center">
                <span className="font-black text-[10px] uppercase tracking-[0.3em] text-blue-600 animate-pulse">Sincronizando Tablero Renovaciones</span>
            </div>
        </div>
    );

    return (
        <div className="flex flex-col h-screen w-full bg-white text-slate-900 overflow-hidden antialiased font-['Inter',_sans-serif] text-left">
            <div className="h-1 bg-blue-900 w-full shrink-0"></div>
            
            {/* CABECERA UNIFICADA */}
            <header className="flex h-20 shrink-0 items-center justify-between border-b border-slate-200 px-8 bg-white z-[110]">
                <div className="flex items-center gap-6">
                    <button type="button" onClick={() => navigate('/cpanel')} className="p-2 text-slate-400 hover:text-slate-800 hover:bg-slate-50 rounded-lg transition-all border border-transparent hover:border-slate-200 shadow-sm"><Icons.ArrowLeft /></button>
                    <h2 className="text-xl font-bold text-slate-800 uppercase tracking-tight">Tablero Renovaciones</h2>
                </div>
                <div className="flex items-center gap-4">
                    <ColumnSelector 
                        columnVisibility={columnVisibility} 
                        columnLabels={columnLabels} 
                        toggleColumn={toggleColumn} 
                    />
                    <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-xl p-1 pr-4 shadow-sm">
                        <div className="bg-blue-900 text-white px-4 py-1.5 rounded-lg text-lg font-black tabular-nums">{filteredData.length}</div>
                        <span className="text-[10px] font-bold uppercase text-slate-500">Casos</span>
                    </div>
                </div>
            </header>

            {/* FILTROS DINÁMICOS */}
            <FiltrosDinamicos 
                key="renovaciones-table-filters"
                config={filterConfig} 
                onFilterChange={handleFilterChange} 
                defaultValues={filterValues} 
            />

            {/* TABLA PRINCIPAL */}
            <main className="flex-grow overflow-hidden flex flex-col bg-white">
                <TableContainer minWidth="2000px">
                    <thead>
                        <tr className="bg-[#01032d] text-white">
                            <SortableHeader label="Vencimiento" sortKey="vencimientoDate" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-32 text-center" />
                            <SortableHeader label="N° Póliza" sortKey="nPoliza" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-32 text-center" />
                            <SortableHeader label="Compañía" sortKey="compania" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-48 text-center" />
                            <SortableHeader label="Cliente" sortKey="displayNombre" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-[320px] text-left" />
                            <SortableHeader label="DNI / CUIT" sortKey="displayDoc" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-32 text-center" />
                            <SortableHeader label="Riesgo / Subriesgo" sortKey="riesgo" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-[250px] text-left" />
                            <SortableHeader label="Forma de Pago" sortKey="formaPago" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-44 text-center" />
                            <SortableHeader label="Estado" sortKey="estadoGestion" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-48 text-center" />
                            <SortableHeader label="Responsable" sortKey="gestionadoPor" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-44 text-center" />
                            <SortableHeader label="Canal" sortKey="canal" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-48 text-left" />
                            <th className="w-48 p-4 sticky right-0 top-0 bg-slate-900 border-b border-slate-700 z-[85] uppercase tracking-wider text-[10px] font-semibold text-center text-white shadow-[-2px_0_5px_rgba(0,0,0,0.2)]">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {paginatedData.map(item => {
                            const isLocked = checkIsItemLocked(item, user?.uid);
                            const isLockedByMe = item.bloqueo && item.bloqueo.userId === user?.uid;

                            return (
                                <TableRow 
                                    key={item.id} 
                                    p={item} 
                                    isSelected={selectedRowId === item.id} 
                                    isLocked={isLocked}
                                    isLockedByMe={isLockedByMe}
                                    columnVisibility={columnVisibility} 
                                    onSelect={setSelectedRowId} 
                                    onAction={handleAction} 
                                    onUnlock={unlockPoliza} 
                                    onShowDebt={handleShowDebt} 
                                    onShowSiniestro={handleShowSiniestro} // 🟢 PASAMOS LA FUNCIÓN
                                />
                            );
                        })}
                    </tbody>
                </TableContainer>

                {/* PIE DE PAGINACIÓN */}
                <PaginatorFooter 
                    currentPage={currentPage}
                    totalPages={totalPages}
                    currentLength={paginatedData.length}
                    totalLength={filteredData.length}
                    onPageChange={setCurrentPage}
                    label="resultados"
                    accentClass="text-blue-900 bg-blue-50 border-blue-100"
                />
            </main>

            {/* MODALES RECONECTADOS */}
            <ModalDetalleDeuda show={showDebtModal} onClose={() => setShowDebtModal(false)} poliza={activePoliza} client={activePoliza ? { id: activePoliza.clienteId, ...(clientsCache[activePoliza.clienteId] || {}) } : null} />
            <ModalDetallePoliza show={showDetailModal} onClose={() => { setShowDetailModal(false); setActivePoliza(null); }} poliza={activePoliza} />
            <ModalFichaCliente show={showFichaModal} onClose={() => { setShowFichaModal(false); setActivePoliza(null); }} client={activePoliza ? { id: activePoliza.clienteId, ...(clientsCache[activePoliza.clienteId] || {}) } : null} />
            
            {/* 🟢 MODAL DE DETALLE COMPLETO DE SINIESTROS */}
            <ModalDetalleSiniestros
                show={showSiniestroModal} 
                onClose={() => setShowSiniestroModal(false)} 
                poliza={siniestroPoliza} 
                client={siniestroClient} 
                siniestros={siniestrosTarget} 
            />

            {activePoliza && (
                <>
                    <EditarRenovacion 
                        show={showEditModal} 
                        onClose={() => { unlockPoliza(activePoliza.path); setShowEditModal(false); setActivePoliza(null); }} 
                        poliza={activePoliza} 
                        freshClientData={clientsCache[activePoliza.clienteId] || { id: activePoliza.clienteId }} 
                        onSuccess={() => { unlockPoliza(activePoliza.path); setShowEditModal(false); setActivePoliza(null); }} 
                    />
                    <RenovarPoliza 
                        show={showRenewModal} 
                        onClose={() => { unlockPoliza(activePoliza.path); setShowRenewModal(false); setActivePoliza(null); }} 
                        poliza={activePoliza} 
                        freshClientData={clientsCache[activePoliza.clienteId] || { id: activePoliza.clienteId }} 
                        onSuccess={() => { unlockPoliza(activePoliza.path); onRenovacionSuccess?.(activePoliza); setShowRenewModal(false); setActivePoliza(null); }} 
                    />
                </>
            )}
        </div>
    );
};

export default RenovacionPolizas;